import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ElectionService, MemberService, VoteService, VoteGroupService } from '../services/firebaseService';
import { Election, Member, Vote, VoteGroup } from '../types';
import { 
  UsersIcon, 
  ClipboardDocumentListIcon, 
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ClockIcon,
  TrophyIcon,
  DocumentArrowUpIcon,
  EnvelopeIcon,
  CalendarIcon,
  EyeIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const AdminDashboard: React.FC = () => {
  const [elections, setElections] = useState<Election[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [voteGroups, setVoteGroups] = useState<VoteGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    const loadData = async () => {
      try {
        const [electionsData, membersData, votesData, groupsData] = await Promise.all([
          ElectionService.getAllElections(),
          MemberService.getAllMembers(),
          VoteService.getAllVotes(),
          VoteGroupService.getAllVoteGroups()
        ]);
        
        setElections(electionsData);
        setMembers(membersData);
        setVotes(votesData);
        setVoteGroups(groupsData);
        setLastRefresh(new Date());
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    // Actualisation automatique toutes les 30 secondes
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculs de statistiques
  const activeElections = elections.filter(e => e.isActive);
  const activeMembers = members.filter(m => m.membershipStatus === 'active' && m.hasVotingRights);
  const activeGroups = voteGroups.filter(g => g.isActive);
  const totalVotes = votes.length;
  const uniqueVoters = new Set(votes.map(v => v.memberId)).size;
  const abstentions = votes.filter(v => v.isAbstention).length;
  const validVotes = totalVotes - abstentions;
  
  // Votes des dernières 24h
  const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const recentVotes = votes.filter(v => v.timestamp > last24h);
  
  // Taux de participation global
  const participationRate = activeMembers.length > 0 ? (uniqueVoters / activeMembers.length) * 100 : 0;
  
  // Élections avec le plus de votes
  const electionStats = elections.map(election => {
    const electionVotes = votes.filter(v => v.electionId === election.id);
    return {
      election,
      totalVotes: electionVotes.length,
      uniqueVoters: new Set(electionVotes.map(v => v.memberId)).size,
      abstentions: electionVotes.filter(v => v.isAbstention).length
    };
  }).sort((a, b) => b.totalVotes - a.totalVotes);
  
  // Groupes avec le plus de votes
  const groupStats = voteGroups.map(group => {
    const groupVotes = votes.filter(v => v.groupId === group.id);
    return {
      group,
      totalVotes: groupVotes.length,
      uniqueVoters: new Set(groupVotes.map(v => v.memberId)).size
    };
  }).sort((a, b) => b.totalVotes - a.totalVotes);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Erreur</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* En-tête avec actualisation */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Administrateur</h1>
            <p className="mt-2 text-gray-600">
              Vue d'ensemble du système de vote ASPROCOM
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              Dernière actualisation: {lastRefresh.toLocaleTimeString('fr-FR')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Actualiser maintenant
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-10 w-10 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-600">Membres actifs</p>
              <p className="text-3xl font-bold text-blue-900">{activeMembers.length}</p>
              <p className="text-xs text-blue-700">
                {members.length} total • {((activeMembers.length / members.length) * 100).toFixed(1)}% actifs
              </p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-10 w-10 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-green-600">Total des votes</p>
              <p className="text-3xl font-bold text-green-900">{totalVotes}</p>
              <p className="text-xs text-green-700">
                {uniqueVoters} votants uniques • {participationRate.toFixed(1)}% participation
              </p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClipboardDocumentListIcon className="h-10 w-10 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-600">Élections actives</p>
              <p className="text-3xl font-bold text-purple-900">{activeElections.length}</p>
              <p className="text-xs text-purple-700">
                {elections.length} total • {activeGroups.length} groupes actifs
              </p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-10 w-10 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-orange-600">Votes 24h</p>
              <p className="text-3xl font-bold text-orange-900">{recentVotes.length}</p>
              <p className="text-xs text-orange-700">
                {abstentions} abstentions • {validVotes} votes valides
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides et outils */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Cog6ToothIcon className="w-5 h-5 mr-2 text-gray-600" />
            Actions rapides
          </h3>
          <div className="space-y-3">
            <Link
              to="/admin/elections"
              className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 border border-blue-200"
            >
              <ClipboardDocumentListIcon className="w-5 h-5 mr-3" />
              Gérer les élections
            </Link>
            <Link
              to="/admin/members"
              className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200 border border-green-200"
            >
              <UsersIcon className="w-5 h-5 mr-3" />
              Gérer les membres
            </Link>
            <Link
              to="/admin/vote-groups"
              className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200 border border-purple-200"
            >
              <UserGroupIcon className="w-5 h-5 mr-3" />
              Groupes de vote
            </Link>
            <Link
              to="/admin/results"
              className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-200 border border-orange-200"
            >
              <ChartBarIcon className="w-5 h-5 mr-3" />
              Voir les résultats
            </Link>
            <Link
              to="/admin/live-tracking"
              className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 border border-red-200"
            >
              <ClockIcon className="w-5 h-5 mr-3" />
              Suivi temps réel
            </Link>
            <Link
              to="/admin/profile"
              className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200 border border-indigo-200"
            >
              <UsersIcon className="w-5 h-5 mr-3" />
              Mon profil
            </Link>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <TrophyIcon className="w-5 h-5 mr-2 text-gray-600" />
            Top élections
          </h3>
          {electionStats.length > 0 ? (
            <div className="space-y-3">
              {electionStats.slice(0, 3).map((stat, index) => (
                <div key={stat.election.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-lg font-bold text-gray-600 mr-3">#{index + 1}</span>
                    <div>
                      <p className="font-medium text-gray-900">{stat.election.title}</p>
                      <p className="text-sm text-gray-500">{stat.election.position}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">{stat.totalVotes}</p>
                    <p className="text-xs text-gray-500">votes</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Aucune élection avec des votes</p>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserGroupIcon className="w-5 h-5 mr-2 text-gray-600" />
            Groupes actifs
          </h3>
          {activeGroups.length > 0 ? (
            <div className="space-y-3">
              {activeGroups.slice(0, 3).map((group) => {
                const groupStat = groupStats.find(gs => gs.group.id === group.id);
                return (
                  <div key={group.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{group.name}</p>
                      <p className="text-sm text-gray-500">{group.elections.length} élection(s)</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Actif
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {groupStat?.totalVotes || 0} votes
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Aucun groupe actif</p>
          )}
        </div>
      </div>

      {/* Élections détaillées et activité récente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2 text-gray-600" />
              Élections en cours
            </h3>
            <Link
              to="/admin/elections"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Voir toutes →
            </Link>
          </div>
          
          {activeElections.length > 0 ? (
            <div className="space-y-4">
              {activeElections.map((election) => {
                const electionStat = electionStats.find(es => es.election.id === election.id);
                const participation = activeMembers.length > 0 ? 
                  ((electionStat?.uniqueVoters || 0) / activeMembers.length) * 100 : 0;
                
                return (
                  <div key={election.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{election.title}</h4>
                        <p className="text-sm text-gray-500">{election.position}</p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Période</p>
                        <p className="font-medium">
                          {election.startDate.toLocaleDateString()} - {election.endDate.toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Candidats</p>
                        <p className="font-medium">{election.candidates.length}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Votes</p>
                        <p className="font-medium">{electionStat?.totalVotes || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Participation</p>
                        <p className="font-medium">{participation.toFixed(1)}%</p>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex justify-end">
                      <Link
                        to={`/admin/results?election=${election.id}`}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        Voir les résultats
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune élection active</h3>
              <p className="mt-1 text-sm text-gray-500">
                Créez une nouvelle élection pour commencer.
              </p>
              <div className="mt-4">
                <Link
                  to="/admin/elections"
                  className="btn-primary"
                >
                  Créer une élection
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <ClockIcon className="w-5 h-5 mr-2 text-gray-600" />
              Activité récente
            </h3>
            <span className="text-sm text-gray-500">24h</span>
          </div>
          
          {recentVotes.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <ChartBarIcon className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <p className="font-medium text-green-900">Nouveaux votes</p>
                    <p className="text-sm text-green-700">Dernières 24 heures</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-900">{recentVotes.length}</p>
                  <p className="text-xs text-green-700">votes</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <UsersIcon className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="font-medium text-blue-900">Nouveaux votants</p>
                    <p className="text-sm text-blue-700">Dernières 24 heures</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-900">
                    {new Set(recentVotes.map(v => v.memberId)).size}
                  </p>
                  <p className="text-xs text-blue-700">votants</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mr-3" />
                  <div>
                    <p className="font-medium text-orange-900">Abstentions</p>
                    <p className="text-sm text-orange-700">Dernières 24 heures</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-900">
                    {recentVotes.filter(v => v.isAbstention).length}
                  </p>
                  <p className="text-xs text-orange-700">abstentions</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune activité récente</h3>
              <p className="mt-1 text-sm text-gray-500">
                Aucun vote enregistré dans les dernières 24 heures.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Outils d'administration */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Cog6ToothIcon className="w-5 h-5 mr-2 text-gray-600" />
          Outils d'administration
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/members"
            className="flex items-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200 border border-blue-200"
          >
            <DocumentArrowUpIcon className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-blue-900">Import CSV</p>
              <p className="text-sm text-blue-700">Importer des membres</p>
            </div>
          </Link>
          
          <Link
            to="/admin/vote-groups"
            className="flex items-center p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200 border border-green-200"
          >
            <EnvelopeIcon className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="font-medium text-green-900">Envoi emails</p>
              <p className="text-sm text-green-700">Liens magiques</p>
            </div>
          </Link>
          
          <Link
            to="/admin/results"
            className="flex items-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200 border border-purple-200"
          >
            <ChartBarIcon className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <p className="font-medium text-purple-900">Rapports</p>
              <p className="text-sm text-purple-700">Analyses détaillées</p>
            </div>
          </Link>
          
          <Link
            to="/admin/members"
            className="flex items-center p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors duration-200 border border-orange-200"
          >
            <UsersIcon className="w-8 h-8 text-orange-600 mr-3" />
            <div>
              <p className="font-medium text-orange-900">Gestion</p>
              <p className="text-sm text-orange-700">Membres & permissions</p>
            </div>
          </Link>
          
          <Link
            to="/admin/live-tracking"
            className="flex items-center p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 border border-red-200"
          >
            <ClockIcon className="w-8 h-8 text-red-600 mr-3" />
            <div>
              <p className="font-medium text-red-900">Suivi Live</p>
              <p className="text-sm text-red-700">Temps réel</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
